import {useCallback, useEffect} from 'react';
import {SubscribeToDropResponse} from '../../../grpc_client/grips/protos/email_subscriptions/v1/email_subscriptions_service';
import {useUserSessionContext} from '../../../ui/sharedContexts/UserSessionContext';
import {State} from '../../../utils/hooks/useAsyncOperation';
import {useMemoizedFunction} from '../../../utils/hooks/useMemoizedFunction';
import {Log} from '../../../utils/logger/log';
import {useDropsSubscriptionsContext} from '../context/SubscriptionsProvider';
import {DropsSubscriptionSchema} from '../types';

export const useUpdateIndividualStoredSubscriptions = ({
  state,
  preferredEmail,
  dropId,
}: {
  state: State<SubscribeToDropResponse, Error>;
  preferredEmail: string;
  dropId: number;
}) => {
  const {email: userEmail} = useUserSessionContext();
  const {ayUser, guest, setSubscriptions} = useDropsSubscriptionsContext();
  const setSubscriptionsMemoized = useMemoizedFunction(setSubscriptions);

  const updateCallback = useCallback(() => {
    if (state.state === 'success') {
      const allDrops = userEmail ? {...ayUser?.allDrops} : {...guest?.allDrops};
      const individualDrops = userEmail
        ? {...ayUser?.individualDrops}
        : {...guest?.individualDrops};
      const customEmails = userEmail ? ayUser?.customEmails || {} : {};
      const isSubscribedWithEmailDifferentThanLoggedIn =
        userEmail && preferredEmail !== userEmail;

      let userIndividualDrops = {
        ...individualDrops,
      };

      if (isSubscribedWithEmailDifferentThanLoggedIn) {
        if (!customEmails[preferredEmail]) {
          customEmails[preferredEmail] = [dropId];
        } else {
          const subscribedIds = customEmails[preferredEmail];

          if (!subscribedIds.includes(dropId)) {
            customEmails[preferredEmail].push(dropId);
          }
        }
      } else {
        userIndividualDrops = {
          ...userIndividualDrops,
          [dropId]: true,
        };
      }

      const subscription = {} as DropsSubscriptionSchema;

      if (userEmail) {
        subscription.ayUser = {
          allDrops,
          customEmails,
          individualDrops: userIndividualDrops,
          email: isSubscribedWithEmailDifferentThanLoggedIn
            ? userEmail
            : preferredEmail,
        };
      } else {
        subscription.guest = {
          allDrops,
          individualDrops: {
            ...individualDrops,
            [dropId]: true,
          },
          email: preferredEmail,
        };
      }

      setSubscriptionsMemoized.current(subscription);
    }

    if (state.state === 'error') {
      Log.error(state.error.message, {
        error: new Error(state.error.name),
        statusCode: state.error.grpcErrorCode,
      });
    }
  }, [
    state.state,
    userEmail,
    preferredEmail,
    ayUser,
    guest,
    setSubscriptionsMemoized,
  ]);

  useEffect(() => {
    updateCallback();
  }, [state.state]);
};
